<script lang="ts">
  import "swiper/css";
  import Marquee from "svelte-fast-marquee";
  import GridContainer from "$lib/molecules/GridContainer.svelte";
  import GridItem from "$lib/atoms/GridItem.svelte";
  import { storyblokEditable } from "@storyblok/svelte";
  import { BREAKPOINTS } from "$lib/utils/breakpoints";
  import type { LogoGridStoryblok } from "$types/components";
  import { logoWidth } from "$stores";
  import { onMount } from "svelte";
  import optimizeImage, { aspectRatio } from "$lib/utils/optimizeImage";

  export let blok: LogoGridStoryblok;
  export let type: "hero" | "slider" | "default" = "default";
  if (blok?.isSlider === true) {
    type = "slider";
  }

  let section: HTMLElement;

  function resize() {
    if (!section) return;
    const logo = section.querySelector(".logo");
    if (logo && logo?.clientWidth > 0) {
      logoWidth.set(logo.clientWidth);
    }
  }

  onMount(() => {
    resize();
  });

  let desktopMediaQuery: MediaQueryList;
  let isMobile = true;
  onMount(() => {
    desktopMediaQuery = window.matchMedia(
      `(min-width: ${BREAKPOINTS.small}px)`,
    );
    desktopMediaQuery.addEventListener("change", onMediaQueryChange);

    onMediaQueryChange();
  });

  function onMediaQueryChange() {
    isMobile = !desktopMediaQuery.matches;
  }
</script>

<svelte:window on:resize={resize} />

{#if type === "hero"}
  <div
    class="flex flex-wrap ml-[-1.25rem] mb-[-1.25rem] sm:ml-[-24px] sm:mb-[-24px]"
  >
    {#if blok.items}
      {#each blok.items as logo}
        <div
          class="pl-[1.25rem] pb-[1.25rem] sm:pl-[24px] sm:pb-[24px] w-1/3 flex flex-col max-w-[280px]"
        >
          <div
            class="logo logo-hero h-full"
            style="border:1px solid {!logo?.borderColor ||
            logo?.borderColor === 'default' ||
            logo?.borderColor === 'light'
              ? 'rgba(255,255,255,0.2)'
              : 'var(--color-stroke-dark-rgba)'}"
          >
            {#if logo.media}
              <img
                class="w-[48%] !max-w-[48%] !max-h-[80%]"
                src={optimizeImage(logo.media.filename)}
                alt={logo.media.alt}
                style={aspectRatio(logo.media.filename)}
              />
            {/if}
          </div>
        </div>
      {/each}
    {/if}
  </div>
{:else if type === "slider" && blok?.items && blok?.items?.length > 0}
  <div
    class="max-w-[100vw] w-full overflow-visible mt-8 xl:mt-0"
    class:pt-[56px]={(blok.paddingTop ?? "") === "medium"}
    class:sm:pt-[70px]={(blok.paddingTop ?? "") === "medium"}
    class:md:pt-[80px]={(blok.paddingTop ?? "") === "medium"}
    class:lg:pt-[120px]={(blok.paddingTop ?? "") === "medium"}
    class:pb-[56px]={(blok.paddingBottom ?? "") === "medium"}
    class:sm:pb-[70px]={(blok.paddingBottom ?? "") === "medium"}
    class:md:pb-[80px]={(blok.paddingBottom ?? "") === "medium"}
    class:lg:pb-[120px]={(blok.paddingBottom ?? "") === "medium"}
  >
    <Marquee speed={isMobile ? 5 : 14}>
      {#each blok.items.concat(blok.items) as logo}
        <div
          class="mx-[0.625rem] sm:mx-[12px] h-full w-[33vw] sm:w-[20vw] max-w-[280px]"
        >
          <div
            class={`rounded-[6px] w-full h-full aspect-[47/20] flex flex-col items-center justify-center`}
            style="border:1px solid {!logo?.borderColor ||
            logo?.borderColor === 'default' ||
            logo?.borderColor === 'light'
              ? 'rgba(255,255,255,0.2)'
              : 'var(--color-stroke-dark-rgba)'}"
          >
            {#if logo.media}
              <img
                src={optimizeImage(logo.media.filename)}
                alt={logo.media.alt}
                class="max-h-[75%] max-w-[80%] mx-auto"
                style={aspectRatio(logo.media.filename)}
                loading="lazy"
              />
            {/if}
          </div>
        </div>
      {/each}
    </Marquee>
  </div>
{:else}
  <section
    bind:this={section}
    class="section"
    use:storyblokEditable={blok}
    class:has-text={blok.text}
    class:max-md:pt-0={(blok.paddingTop ?? "") === ""}
    class:md:pt-[70px]={(blok.paddingTop ?? "") === ""}
  >
    <div
      class={`logo-grid ${
        blok?.backgroundColor ? "bg-sand inner-padding rounded-xl" : ""
      } `}
    >
      {#if blok.title}
        <div class="header">
          <GridContainer>
            <GridItem col={12} colMd={blok.text ? 5 : 12}>
              <p class="headline type:h3">{blok.title}</p>
            </GridItem>

            {#if blok.text}
              <GridItem col={12} offset={0} colMd={6} offsetMd={1}>
                <div class="content">
                  <p class="type:body-large">{blok.text}</p>
                </div>
              </GridItem>
            {/if}
          </GridContainer>
        </div>
      {/if}

      <GridContainer>
        <GridItem col={12} colMd={12}>
          <div class="mx-auto max-w-[1200px]">
            <div
              class={`flex flex-wrap -ml-[1.25rem] -mb-[1.25rem] sm:-ml-[24px] sm:-mb-[24px] ${
                blok?.alignLeft === true ? "justify-start" : "justify-center"
              }`}
            >
              {#if blok.items}
                {#each blok.items as logo}
                  <div
                    class="pl-[1.25rem] pb-[1.25rem] sm:pl-[24px] sm:pb-[24px] w-1/2 md:w-1/4 flex flex-col"
                  >
                    <div class="logo h-full">
                      {#if logo.media}
                        <img
                          class="!max-w-[80%] !max-h-[80%]"
                          src={optimizeImage(logo.media.filename)}
                          alt={logo.media.alt}
                          style={aspectRatio(logo.media.filename)}
                          loading="lazy"
                        />
                      {/if}
                    </div>
                  </div>
                {/each}
              {/if}
            </div>
          </div>
        </GridItem>
      </GridContainer>
    </div>
  </section>
{/if}

<style lang="postcss">
  img {
    object-fit: cover;
  }

  .inner-padding {
    padding-top: 48px;
    padding-bottom: 48px;
    @screen md {
      padding-top: 100px;
      padding-bottom: 100px;
    }
  }

  .logo {
    width: 100%;
    aspect-ratio: 47/20;
    border-radius: 6px;
    border: 1px solid rgb(var(--color-stroke-dark));
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 280px;
    max-height: 100%;
    position: relative;
    /* @include breakpoint('small') {
			aspect-ratio: 47/20;
		} */
    &-hero {
      border: 1px solid rgba(255, 255, 255, 0.2);
    }

    img {
      position: absolute;
      inset: 0;
      margin: auto;
      max-width: 154px;
      max-height: 84px;
      display: block;

      @screen sm {
        max-width: 80%;
        max-height: 130px;
      }
    }
  }

  :not(.has-text) {
    .header {
      text-align: center;
      margin: 0 auto 32px;
      @screen md {
        margin: 0 auto 56px;
      }
    }
  }

  .has-text {
    .header {
      margin: 0 auto 24px;
      @screen md {
        margin: 0 auto 120px;
      }
    }

    .headline {
      margin-bottom: 32px;
      @screen md {
        margin-bottom: 56px;
      }

      @screen sm {
        margin: 0;
      }
    }
  }
</style>
